import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Camera, ImageIcon, Loader2, X } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { apiError, fileUrl, productImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SESSION_STATUS_META } from "@/lib/constants";
import { NumInput } from "@/components/NumInput";
import ImageLightbox from "@/components/ImageLightbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";

const EVENT_LABELS = {
  created: "Session created",
  draft_saved: "Draft saved",
  evidence_added: "Evidence photo added",
  evidence_removed: "Evidence photo removed",
  submitted: "Submitted for checker approval",
  approved: "Approved — quantities are now official",
  returned: "Returned for correction",
};

const RETURN_REASONS = [
  "Quantity does not match evidence",
  "Photo unclear",
  "Wrong product received",
  "Need additional evidence",
];

const approverTag = (role, name) =>
  name && name.includes("(") ? null : role === "owner" ? "Owner Override" : role === "admin" || role === "admin_input" ? "Admin Input / Checker" : null;

export default function ReceivingSession() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [actuals, setActuals] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [approving, setApproving] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [returning, setReturning] = useState(false);
  const [note, setNote] = useState("");
  const [pendingPid, setPendingPid] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [zoomImg, setZoomImg] = useState(null);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const cancelSession = async () => {
    if (!confirmCancel) {
      setConfirmCancel(true);
      return;
    }
    try {
      await api.delete(`/receiving/sessions/${sessionId}`);
      toast.success("Draft session cancelled");
      navigate("/receiving");
    } catch (e) {
      toast.error(apiError(e));
      setConfirmCancel(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/receiving/sessions/${sessionId}`);
      setSession(data);
      const a = {};
      for (const it of data.items) a[it.product_id] = it.actual_qty > 0 ? String(it.actual_qty) : "";
      setActuals(a);
      setNote(data.note || "");
    } catch (e) {
      toast.error(apiError(e));
      setSession(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (["owner", "receiving", "admin", "admin_input"].includes(user.role)) load();
  }, [user, load]);

  const totals = useMemo(() => {
    if (!session) return { expected: 0, actual: 0 };
    return {
      expected: session.items.reduce((s, it) => s + it.expected_qty, 0),
      actual: session.items.reduce((s, it) => s + (parseInt(actuals[it.product_id], 10) || 0), 0),
    };
  }, [session, actuals]);

  if (!["owner", "receiving", "admin", "admin_input"].includes(user.role)) {
    return (
      <div data-testid="session-forbidden" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
        <p className="font-medium text-muted-foreground">Receiving is only available to the Owner, Admin Checker, Admin Input, and Receiving PIC.</p>
      </div>
    );
  }

  if (session === null) {
    return (
      <div className="grid place-items-center py-24" data-testid="session-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (session === false) {
    return (
      <div className="grid place-items-center rounded-md border border-dashed py-20 text-center">
        <p className="font-medium text-muted-foreground">Session not found.</p>
      </div>
    );
  }

  const status = session.status;
  const canEnter = ["owner", "receiving"].includes(user.role);
  const editable = (status === "draft" || status === "correction_required") && canEnter;
  const pending = status === "pending_approval";
  const approved = status === "approved";
  const canDecide = pending && (user.role === "owner" || user.role === "admin" || user.role === "admin_input");
  const isSelfSession = user.id === session.pic_id || user.id === session.submitted_by_id;
  const selfBlocked = pending && isSelfSession && user.role !== "owner";
  const meta = SESSION_STATUS_META[status] || SESSION_STATUS_META.draft;

  const save = async () => {
    setSaving(true);
    try {
      const items = session.items.map((it) => ({
        product_id: it.product_id,
        actual_qty: parseInt(actuals[it.product_id], 10) || 0,
      }));
      const { data } = await api.put(`/receiving/sessions/${sessionId}`, { items, note });
      setSession(data);
      toast.success("Draft saved — quantities are not counted as received until Owner approval");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/receiving/sessions/${sessionId}`, {
        items: session.items.map((it) => ({ product_id: it.product_id, actual_qty: parseInt(actuals[it.product_id], 10) || 0 })),
        note,
      });
      const { data } = await api.post(`/receiving/sessions/${sessionId}/submit`);
      setSession(data);
      setSubmitOpen(false);
      toast.success("Submitted for checker approval — quantities are now locked");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const approve = async () => {
    if (!confirmApprove) {
      setConfirmApprove(true);
      return;
    }
    setApproving(true);
    try {
      const { data } = await api.post(`/receiving/sessions/${sessionId}/approve`);
      setSession(data);
      setConfirmApprove(false);
      toast.success("Approved — Receiving Queue updated");
    } catch (e) {
      toast.error(apiError(e));
      setConfirmApprove(false);
    } finally {
      setApproving(false);
    }
  };

  const returnForCorrection = async () => {
    if (!returnReason.trim()) return;
    setReturning(true);
    try {
      const { data } = await api.post(`/receiving/sessions/${sessionId}/return`, { reason: returnReason.trim() });
      setSession(data);
      setReturnOpen(false);
      setReturnReason("");
      toast.success("Returned for correction");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setReturning(false);
    }
  };

  const enteredItems = session.items.filter((it) => (editable ? parseInt(actuals[it.product_id], 10) || 0 : it.actual_qty) > 0);

  const pickEvidence = (pid, mode) => {
    setPendingPid(pid);
    (mode === "camera" ? cameraRef : galleryRef).current?.click();
  };

  const compressImage = async (file) => {
    try {
      const bmp = await createImageBitmap(file);
      const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
      if (scale >= 1 && file.size <= 3 * 1024 * 1024) return file;
      const w = Math.max(1, Math.round(bmp.width * scale));
      const h = Math.max(1, Math.round(bmp.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob || blob.size >= file.size) return file;
      return new File([blob], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
    } catch {
      return file; // graceful fallback — a camera/codec limitation must never break receiving
    }
  };

  const onEvidencePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !pendingPid) return;
    setUploadingFor(pendingPid);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await api.post(`/receiving/sessions/${sessionId}/items/${pendingPid}/evidence`, fd);
      setSession(data);
      toast.success("Evidence photo added");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingFor(null);
      setPendingPid(null);
    }
  };

  const removeEvidence = async (pid, eid) => {
    try {
      const { data } = await api.delete(`/receiving/sessions/${sessionId}/items/${pid}/evidence/${eid}`);
      setSession(data);
      toast.success("Evidence photo removed");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="session-page">
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="evidence-camera-input"
        onChange={onEvidencePicked}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="evidence-gallery-input"
        onChange={onEvidencePicked}
      />
      <button
        data-testid="back-to-receiving"
        onClick={() => navigate("/receiving")}
        className="mb-3 flex h-10 items-center gap-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Receiving Queue
      </button>

      <div className="mb-5">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight" data-testid="session-batch-name">
            {session.batch_name}
          </h1>
          <span data-testid="session-status-badge" className={`rounded-md border px-2 py-0.5 text-xs font-bold ${meta.badge}`}>
            {meta.label}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          Session #{session.id.slice(0, 8)} · {session.pic_name} · {format(new Date(session.created_at), "d MMM yyyy, HH:mm")}
        </p>
        {status === "draft" && (
          <p className="mt-2 rounded-md border border-rose-100 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700" data-testid="session-draft-note">
            Draft quantities are a physical count preview only — the Receiving Queue's Approved Received stays unchanged until Checker approval.
          </p>
        )}
        {status === "correction_required" && (
          <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="session-correction-note">
            Returned by {session.returned_by_name}
            {approverTag(session.returned_by_role, session.returned_by_name) && ` (${approverTag(session.returned_by_role, session.returned_by_name)})`}: “{session.return_reason}” — correct the quantities and resubmit. Official Received unchanged.
          </p>
        )}
        {pending && (
          <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700" data-testid="session-pending-note">
            Submitted by {session.submitted_by_name}
            {session.submitted_at && ` · ${format(new Date(session.submitted_at), "d MMM yyyy, HH:mm")}`} — quantities locked. Official Received unchanged until checker approval.
          </p>
        )}
        {approved && (
          <p className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700" data-testid="session-approved-note">
            Approved by {session.approved_by_name}
            {approverTag(session.approved_by_role, session.approved_by_name) && ` (${approverTag(session.approved_by_role, session.approved_by_name)})`}
            {session.approved_at && ` · ${format(new Date(session.approved_at), "d MMM yyyy, HH:mm")}`} — quantities committed to official Received.
          </p>
        )}
      </div>

      {session.items.length === 0 ? (
        <p data-testid="session-empty" className="rounded-md border border-dashed py-16 text-center text-sm text-muted-foreground">
          No products waiting to receive in this batch
        </p>
      ) : (
        <div className="space-y-3" data-testid="session-items">
          {session.items.map((it) => {
            const actual = editable ? parseInt(actuals[it.product_id], 10) || 0 : it.actual_qty;
            const remainingAfter = it.expected_qty - actual;
            const result = actual <= 0 ? null : remainingAfter === 0 ? "FULL" : "PARTIAL";
            const img = productImage(it);
            return (
              <div key={it.product_id} data-testid={`session-item-${it.product_id}`} className="space-y-3 rounded-md border bg-card p-4">
                <div className="flex gap-3">
                  {img ? (
                    <img src={img} alt={it.product_name} className="h-16 w-16 shrink-0 rounded-md border bg-stone-100 object-cover" />
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border bg-stone-100">
                      <ImageIcon className="h-6 w-6 text-stone-300" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-heading text-base font-bold tracking-tight">{it.product_name}</p>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                      <div className="rounded-md border bg-[#EDE4DE] py-1.5">
                        <p className="text-sm font-bold">{it.purchased_qty}</p>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Purchased</p>
                      </div>
                      <div className="rounded-md border bg-[#EDE4DE] py-1.5">
                        <p className="text-sm font-bold">{it.previously_received_qty}</p>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Prev Received</p>
                      </div>
                      <div className="rounded-md border bg-[#EDE4DE] py-1.5">
                        <p className="text-sm font-bold" data-testid={`expected-${it.product_id}`}>{it.expected_qty}</p>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Expected</p>
                      </div>
                    </div>
                  </div>
                </div>
                {editable ? (
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-muted-foreground">
                      Actual qty received <span className="font-normal">(max {it.expected_qty})</span>
                    </span>
                    <NumInput
                      data-testid={`actual-input-${it.product_id}`}
                      mode="int"
                      className="h-14 flex-1 text-center font-heading text-xl font-extrabold"
                      placeholder="0"
                      value={actuals[it.product_id] ?? ""}
                      onValue={(v) => {
                        if (v !== "" && parseInt(v, 10) > it.expected_qty) return;
                        setActuals((a) => ({ ...a, [it.product_id]: v }));
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-between rounded-md border bg-[#EDE4DE] px-3 py-2">
                    <span className="text-sm text-stone-600">
                      Received this session: <b data-testid={`readonly-actual-${it.product_id}`}>{it.actual_qty}</b>
                      {it.actual_qty > 0 && (
                        <>
                          {" "}· Remaining after: <b>{it.remaining_after}</b>
                        </>
                      )}
                    </span>
                    {it.result && (
                      <span
                        className={`rounded-md border px-2 py-0.5 text-xs font-bold ${
                          it.result === "FULL"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-amber-200 bg-amber-50 text-amber-700"
                        }`}
                      >
                        {it.result}
                      </span>
                    )}
                  </div>
                )}
                {editable && actual > 0 && (
                  <div className="flex items-center justify-between rounded-md border bg-[#EDE4DE] px-3 py-2">
                    <span className="text-sm text-stone-600">
                      Remaining after this session: <b data-testid={`remaining-after-${it.product_id}`}>{remainingAfter}</b>
                    </span>
                    <span
                      data-testid={`result-badge-${it.product_id}`}
                      className={`rounded-md border px-2 py-0.5 text-xs font-bold ${
                        result === "FULL"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-amber-200 bg-amber-50 text-amber-700"
                      }`}
                    >
                      {result}
                    </span>
                  </div>
                )}
                <div className="space-y-2" data-testid={`evidence-section-${it.product_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                      Receiving Evidence{(it.evidence || []).length > 0 && ` (${(it.evidence || []).length})`}
                      <span className="ml-1 normal-case text-muted-foreground/70">· optional</span>
                    </p>
                  </div>
                  {editable && (
                    <div className="flex gap-2">
                      <Button
                        data-testid={`evidence-camera-${it.product_id}`}
                        className="h-12 flex-1 text-sm"
                        onClick={() => pickEvidence(it.product_id, "camera")}
                        disabled={uploadingFor === it.product_id}
                      >
                        {uploadingFor === it.product_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-5 w-5" />}
                        {uploadingFor === it.product_id ? "Uploading…" : (it.evidence || []).length > 0 ? "Take Another Photo" : "Take Photo"}
                      </Button>
                      <Button
                        variant="outline"
                        data-testid={`evidence-gallery-${it.product_id}`}
                        className="h-12"
                        onClick={() => pickEvidence(it.product_id, "gallery")}
                        disabled={uploadingFor === it.product_id}
                      >
                        <ImageIcon className="h-4 w-4" /> Gallery
                      </Button>
                    </div>
                  )}
                  {(it.evidence || []).length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {(it.evidence || []).map((ev) => (
                        <div key={ev.id} className="relative">
                          <button
                            type="button"
                            data-testid={`evidence-thumb-${ev.id}`}
                            aria-label="View evidence photo"
                            onClick={() => setZoomImg({ src: fileUrl(ev.path), alt: `${it.product_name} evidence` })}
                            className="block active:opacity-90"
                          >
                            <img src={fileUrl(ev.path)} alt="Receiving evidence" className="h-20 w-20 rounded-md border bg-stone-100 object-cover" />
                          </button>
                          {editable && (
                            <button
                              type="button"
                              data-testid={`evidence-remove-${ev.id}`}
                              aria-label="Remove evidence photo"
                              onClick={() => removeEvidence(it.product_id, ev.id)}
                              className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white shadow"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground" data-testid={`evidence-empty-${it.product_id}`}>
                      No photos yet — optional, but they help the checker verify the goods
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editable ? (
        <div className="mt-4 space-y-2" data-testid="session-note-block">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Receiving note (optional)</p>
          <Textarea
            data-testid="session-note-input"
            placeholder="e.g. box slightly dented, courier name, delivery time…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      ) : (
        session.note && (
          <div className="mt-4 space-y-1" data-testid="session-note-block">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Receiving note</p>
            <p className="rounded-md border bg-card p-3 text-sm" data-testid="session-note-text">{session.note}</p>
          </div>
        )
      )}

      {session.audit?.length > 0 && (
        <div className="mt-5" data-testid="audit-history">
          <h3 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Audit History</h3>
          <div className="space-y-2">
            {[...session.audit].reverse().map((e, i) => (
              <div key={i} className="rounded-md border bg-card p-3">
                <p className="text-sm font-semibold">{EVENT_LABELS[e.event] || e.event}</p>
                <p className="text-xs text-muted-foreground">
                  {e.actor_name}
                  {approverTag(e.actor_role, e.actor_name) && ` (${approverTag(e.actor_role, e.actor_name)})`} · {format(new Date(e.at), "d MMM yyyy, HH:mm")}
                  {(e.event === "draft_saved" || e.event === "submitted") && e.details?.total_after != null && ` · total ${e.details.total_after}`}
                  {e.event === "submitted" && e.details?.total_received != null && ` · received ${e.details.total_received}`}
                  {e.event === "approved" && e.details?.received_after != null && ` · official received ${e.details.received_before} → ${e.details.received_after}`}
                  {(e.event === "evidence_added" || e.event === "evidence_removed") && e.details?.product_name && ` · ${e.details.product_name}`}
                </p>
                {e.details?.reason && <p className="text-xs font-medium text-red-600">Reason: {e.details.reason}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {(editable || pending) && <div className="h-48 md:h-32" aria-hidden="true" />}

      {editable && (
        <div className="fixed inset-x-0 bottom-20 z-40 px-4 pb-[env(safe-area-inset-bottom)] md:bottom-6">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-2 rounded-md border bg-white/90 p-3 shadow-lg backdrop-blur-xl">
            <span data-testid="session-summary" className="text-sm font-semibold">
              {totals.actual}/{totals.expected} units entered
            </span>
            <div className="ml-auto flex gap-2">
              {status === "draft" && (
                <Button
                  variant={confirmCancel ? "destructive" : "outline"}
                  data-testid="cancel-draft-button"
                  className="h-11"
                  onClick={cancelSession}
                >
                  {confirmCancel ? "Confirm Cancel" : "Cancel"}
                </Button>
              )}
              <Button variant="outline" data-testid="save-draft-button" className="h-11" onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save Draft"}
              </Button>
              <Button
                data-testid="submit-approval-button"
                className="h-11"
                onClick={() => setSubmitOpen(true)}
                disabled={totals.actual === 0}
              >
                <span className="hidden min-[480px]:inline">Submit for Checker Approval</span>
                <span className="min-[480px]:hidden">Submit</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {canDecide && (
        <div className="fixed inset-x-0 bottom-20 z-40 px-4 pb-[env(safe-area-inset-bottom)] md:bottom-6">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-2 rounded-md border bg-white/90 p-3 shadow-lg backdrop-blur-xl">
            {selfBlocked && (
              <span data-testid="self-approval-blocked-note" className="w-full text-xs font-semibold text-red-600">
                You created or submitted this session — approval requires a different Checker or Owner Override.
              </span>
            )}
            <span className="text-sm font-semibold" data-testid="pending-summary">
              {session.items.reduce((s, it) => s + it.actual_qty, 0)} units submitted
            </span>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" data-testid="return-session-button" className="h-11" onClick={() => setReturnOpen(true)}>
                Return for Correction
              </Button>
              <Button
                data-testid="approve-session-button"
                className={`h-11 ${confirmApprove ? "" : "bg-emerald-600 hover:bg-emerald-700"}`}
                variant={confirmApprove ? "default" : "default"}
                onClick={approve}
                disabled={approving || selfBlocked}
              >
                {approving ? "Approving…" : confirmApprove ? "Confirm Approve" : "Approve"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Drawer open={submitOpen} onOpenChange={setSubmitOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Submit for Checker Approval?</DrawerTitle>
          </DrawerHeader>
          <div className="max-h-[70vh] space-y-3 overflow-y-auto px-4 pb-8" data-testid="submit-summary">
            {enteredItems.map((it) => {
              const actual = parseInt(actuals[it.product_id], 10) || 0;
              const remaining = it.expected_qty - actual;
              const result = remaining === 0 ? "FULL" : "PARTIAL";
              return (
                <div key={it.product_id} className="rounded-md border bg-card p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold">{it.product_name}</p>
                    <span
                      className={`rounded-md border px-2 py-0.5 text-xs font-bold ${
                        result === "FULL"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-amber-200 bg-amber-50 text-amber-700"
                      }`}
                    >
                      {result}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Purchased {it.purchased_qty} · Prev received {it.previously_received_qty} · This session {actual} · Remaining {remaining}
                  </p>
                  <p
                    className="mt-1 text-xs font-semibold text-stone-500"
                    data-testid={`submit-evidence-${it.product_id}`}
                  >
                    Evidence: {(it.evidence || []).length} photo{(it.evidence || []).length === 1 ? "" : "s"} (optional)
                  </p>
                </div>
              );
            })}
            <p className="rounded-md border border-rose-100 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
              Quantities will be locked until the Checker approves or returns this session. Official Received stays unchanged until approval.
            </p>
            <Button data-testid="submit-confirm-button" className="h-12 w-full text-base" onClick={submit} disabled={submitting}>
              {submitting ? "Submitting…" : "Confirm Submit"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={returnOpen} onOpenChange={setReturnOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Return for Correction</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 px-4 pb-8">
            <p className="text-sm text-muted-foreground">
              The Receiving PIC will be able to edit quantities and evidence again. Official Received stays unchanged. Your reason is recorded in the audit history.
            </p>
            <div className="flex flex-wrap gap-2">
              {RETURN_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`return-reason-chip-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setReturnReason(r)}
                  className={`h-9 rounded-full border px-3 text-xs font-semibold transition-colors active:scale-[0.98] ${
                    returnReason === r ? "border-red-500 bg-red-500 text-white" : "bg-card text-stone-600 hover:bg-accent"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <Textarea
              data-testid="return-reason-input"
              placeholder="Reason (required) — e.g. counted 5 but only 4 in the box"
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
            />
            <Button
              variant="destructive"
              data-testid="return-confirm-button"
              className="h-12 w-full text-base"
              onClick={returnForCorrection}
              disabled={returning || !returnReason.trim()}
            >
              {returning ? "Returning…" : "Confirm Return"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {zoomImg && <ImageLightbox src={zoomImg.src} alt={zoomImg.alt} onClose={() => setZoomImg(null)} />}
    </div>
  );
}
